import type { ReactNode } from 'react'

import badgeStatic from '@/assets/logo/badge-static.svg'
import { Logo } from '@/components/layout/logo.atom'
import { cn } from '@/lib/utils'

import { AuthFeatureItemAtom } from './auth-feature-item.atom'
import { AUTH_FEATURE_LIST, AUTH_TAGLINE } from './auth-features.const'
import { AuthFloatingIcons } from './auth-floating-icons.atom'
import type { AuthBrandPanelProps } from './auth-layout.interface'
import { useMouseParallax } from './use-mouse-parallax.hook'

export const AuthBrandPanel: React.FC<AuthBrandPanelProps> = ({
  heading,
  tagline = AUTH_TAGLINE,
  featureList = AUTH_FEATURE_LIST,
  className,
}): ReactNode => {
  const panelRef = useMouseParallax()

  return (
    <div
      ref={panelRef}
      className={cn(
        'auth-brand-panel relative flex flex-col gap-6 overflow-hidden p-8 pb-10 lg:items-center lg:justify-center lg:p-12 lg:pt-24',
        'bg-[linear-gradient(135deg,var(--saaga-primary-800),var(--saaga-primary-600),var(--saaga-primary-400))]',
        className
      )}
    >
      <AuthFloatingIcons />

      <img
        src={badgeStatic}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -bottom-16 h-64 w-64 opacity-10 brightness-0 invert"
      />

      <Logo className="absolute top-12 left-12 hidden h-7 w-auto brightness-0 invert lg:block" />

      <div className="relative z-10 max-w-md space-y-6 xl:max-w-lg">
        <div className="space-y-4">
          <h1 className="text-3xl leading-tight font-bold tracking-tight text-white lg:text-[2rem]">
            {heading}
          </h1>
          {tagline && (
            <p className="inline-block rounded-full bg-white/15 px-4 py-1.5 text-sm font-medium text-white">
              {tagline}
            </p>
          )}
        </div>

        {featureList.length > 0 && (
          <div className="auth-feature-list space-y-4">
            {featureList.map((feature) => (
              <AuthFeatureItemAtom key={feature.title} {...feature} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
